import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import { getMyRequests } from '../../api/serviceRequests.api';
import { getMyMembership } from '../../api/rsa.api';
import '../../styles/user-dashboard.css';

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  dispatched: 'Dispatched',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const QuickAction = ({ to, icon, label, color }) => (
  <Link to={to} className="user-quick-action">
    <div className="user-quick-action-icon" style={{ background: color + '20' }}>
      <svg className="w-5 h-5" fill="none" stroke={color} viewBox="0 0 24 24">{icon}</svg>
    </div>
    <div className="user-quick-action-label">{label}</div>
  </Link>
);

const Dashboard = () => {
  const { user } = useUserAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0 });
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [membership, setMembership] = useState(undefined); // undefined = loading, null = none

  useEffect(() => {
    getMyRequests({ limit: 5 })
      .then((data) => {
        const reqs = data.requests || [];
        setRequests(reqs);
        setStats({
          total: data.pagination?.total || reqs.length,
          active: reqs.filter(r => !['completed', 'cancelled'].includes(r.status)).length,
          completed: reqs.filter(r => r.status === 'completed').length,
        });
      })
      .catch(() => {})
      .finally(() => setLoadingReqs(false));

    getMyMembership()
      .then((m) => setMembership(m || null))
      .catch(() => setMembership(null));
  }, []);

  const membershipLabel =
    membership === undefined ? '…' : membership ? (membership.plan?.name || 'Active') : 'None';

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const firstName = user?.name?.split(' ')[0] || 'User';

  return (
    <div className="user-dashboard">
      <div className="user-dashboard-container">
        <div className="user-dashboard-header">
          <div className="user-dashboard-greeting">Good day, {firstName}!</div>
          <div className="user-dashboard-date">{today}</div>
        </div>

        <div className="user-stats-grid">
          <div className="user-stat-card">
            <div className="user-stat-icon blue">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <div className="user-stat-value">{stats.total}</div>
              <div className="user-stat-label">Total Requests</div>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon amber">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="user-stat-value">{stats.active}</div>
              <div className="user-stat-label">Active Requests</div>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon green">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="user-stat-value">{stats.completed}</div>
              <div className="user-stat-label">Completed</div>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon purple">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <div className="user-stat-value" style={{ fontSize: membershipLabel.length > 6 ? '1.1rem' : undefined }}>{membershipLabel}</div>
              <div className="user-stat-label">RSA Membership</div>
            </div>
          </div>
        </div>

        <h3 className="user-section-title">Quick Actions</h3>
        <div className="user-quick-actions">
          <QuickAction to="/book-service" color="#2563eb" label="Book Service"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />}
          />
          <QuickAction to="/my-bookings" color="#7c3aed" label="My Bookings"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />}
          />
          <QuickAction to="/rsa-join" color="#059669" label="Buy RSA Plan"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
          />
          <QuickAction to="/track-service" color="#d97706" label="Track Service"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />}
          />
          <QuickAction to="/profile" color="#64748b" label="My Profile"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />}
          />
        </div>

        <h3 className="user-section-title">Recent Requests</h3>
        <div className="user-recent-list">
          {loadingReqs ? (
            <div className="user-empty-state">
              <div className="auth-spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : requests.length === 0 ? (
            <div className="user-empty-state">
              <div className="user-empty-icon">📋</div>
              <div className="user-empty-text">No service requests yet</div>
              <Link to="/book-service" className="user-cta-link">
                Book your first service
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ) : (
            requests.map((req) => (
              <div key={req.id} className="user-recent-item">
                <div className="user-recent-service">
                  <div className="user-recent-service-name">{req.serviceName || req.service?.name || 'Roadside Service'}</div>
                  <div className="user-recent-service-date">
                    {new Date(req.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {req.trackingId && <> &bull; <span style={{ fontFamily: 'monospace' }}>{req.trackingId}</span></>}
                  </div>
                </div>
                <span className={`user-status-badge ${req.status}`}>
                  {STATUS_LABELS[req.status] || req.status}
                </span>
              </div>
            ))
          )}
        </div>

        {requests.length > 0 && (
          <Link to="/my-bookings" className="user-cta-link">
            View all bookings
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
